import React, { useState, useRef, useEffect, useContext } from "react";
import axios from "axios";
import { DocumentContext } from "./DocumentContext";
import {
  FileText,
  Upload,
  Eye,
  CheckCircle,
  Clock,
  AlertCircle,
  X,
  File,
  Shield,
  IdCard,
  Wallet,
  UserCheck,
  Home,
} from "lucide-react";

const Documents = () => {
  const StudentUserId = sessionStorage.getItem("StudentUserId");
  const { setUploadedCount, setDocumentsState } = useContext(DocumentContext);

  const [documents, setDocuments] = useState({
    idOrPassport: { file: null, documentId: null, status: "Not Uploaded", fileName: null },
    proofOfFunding: { file: null, documentId: null, status: "Not Uploaded", fileName: null },
    nextOfKinID: { file: null, documentId: null, status: "Not Uploaded", fileName: null },
    proofOfResidence: { file: null, documentId: null, status: "Not Uploaded", fileName: null },
  });

  const [previewFile, setPreviewFile] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);

  const fileRefs = {
    idOrPassport: useRef(),
    proofOfFunding: useRef(),
    nextOfKinID: useRef(),
    proofOfResidence: useRef(),
  };

  const calculateUploadedCount = (docs) =>
    Object.values(docs).filter((doc) => doc.documentId).length;

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!StudentUserId) return;
      try {
        const res = await axios.get(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/StudentDocuments/${StudentUserId}`);
        const updatedDocs = { ...documents };

        res.data.forEach((doc) => {
          if (updatedDocs[doc.documentType]) {
            updatedDocs[doc.documentType] = {
              ...updatedDocs[doc.documentType],
              documentId: doc.documentId,
              status: doc.status === "Approved" ? "Approved" : (doc.status || "Uploaded"),
              fileName: doc.filePath?.split("\\").pop().split("/").pop() || null,
            };
          }
        });

        setDocuments(updatedDocs);
        setUploadedCount(calculateUploadedCount(updatedDocs));
        setDocumentsState(updatedDocs);
      } catch (err) {
        console.error("Error fetching documents:", err);
      }
    };

    fetchDocuments();
  }, [StudentUserId, setUploadedCount, setDocumentsState]);

  const handleFileChange = (e, key) => {
    const file = e.target.files[0];
    if (file) {
      const tempUrl = URL.createObjectURL(file);
      setDocuments((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          file,
          status: "Pending Upload",
          fileName: file.name,
          tempPreview: tempUrl,
        },
      }));
    }
  };

  const handleUpload = async (key) => {
    if (!StudentUserId) return;
    const docFile = documents[key].file;
    if (!docFile) return;

    setUploadingKey(key);
    const formData = new FormData();
    formData.append("file", docFile);

    try {
      const res = await axios.post(
        `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/StudentDocuments/upload/${StudentUserId}/${key}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      setDocuments((prev) => {
        const newDocs = {
          ...prev,
          [key]: {
            ...prev[key],
            status: "Uploaded",
            documentId: res.data.documentId,
            file: null,
            tempPreview: null,
          },
        };
        setUploadedCount(calculateUploadedCount(newDocs));
        setDocumentsState(newDocs);
        return newDocs;
      });
    } catch (err) {
      console.error(`Upload failed for ${key}:`, err);
    } finally {
      setUploadingKey(null);
    }
  };

  const handleView = (key) => {
    const doc = documents[key];
    if (!doc.file && !doc.documentId) return;

    const fileUrl = doc.file ? doc.tempPreview : `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/StudentDocuments/view/${doc.documentId}`;
    const isPdf = doc.fileName?.toLowerCase().endsWith(".pdf");

    setPreviewType(isPdf ? "pdf" : "image");
    setPreviewFile(fileUrl);
  };

  const closeModal = () => {
    setPreviewFile(null);
    setPreviewType(null);
  };

  const documentList = [
    {
      key: "idOrPassport",
      label: "ID / Passport",
      description: "Valid government-issued identification document",
      icon: <IdCard className="w-5 h-5" />,
      accent: "blue",
    },
    {
      key: "proofOfFunding",
      label: "Proof of Funding",
      description: "Bank statement, scholarship letter, or sponsorship confirmation",
      icon: <Wallet className="w-5 h-5" />,
      accent: "emerald",
    },
    {
      key: "nextOfKinID",
      label: "Next of Kin ID",
      description: "Emergency contact's valid identification",
      icon: <UserCheck className="w-5 h-5" />,
      accent: "violet",
    },
    {
      key: "proofOfResidence",
      label: "Proof of Residence",
      description: "Utility bill, lease agreement, or official address confirmation",
      icon: <Home className="w-5 h-5" />,
      accent: "amber",
    },
  ];

  const accentStyles = {
    blue: { iconBg: "bg-blue-100", iconText: "text-blue-600", border: "border-l-blue-500", hover: "hover:bg-blue-50" },
    emerald: { iconBg: "bg-emerald-100", iconText: "text-emerald-600", border: "border-l-emerald-500", hover: "hover:bg-emerald-50" },
    violet: { iconBg: "bg-violet-100", iconText: "text-violet-600", border: "border-l-violet-500", hover: "hover:bg-violet-50" },
    amber: { iconBg: "bg-amber-100", iconText: "text-amber-600", border: "border-l-amber-500", hover: "hover:bg-amber-50" },
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "Approved":
        return {
          bg: "bg-green-50",
          text: "text-green-700",
          border: "border-green-200",
          icon: <CheckCircle className="w-3.5 h-3.5" />,
          label: "Approved",
        };
      case "Uploaded":
        return {
          bg: "bg-blue-50",
          text: "text-blue-700",
          border: "border-blue-200",
          icon: <CheckCircle className="w-3.5 h-3.5" />,
          label: "Uploaded",
        };
      case "Pending Upload":
        return {
          bg: "bg-amber-50",
          text: "text-amber-700",
          border: "border-amber-200",
          icon: <Clock className="w-3.5 h-3.5" />,
          label: "Pending",
        };
      default:
        return {
          bg: "bg-gray-100",
          text: "text-gray-500",
          border: "border-gray-200",
          icon: <AlertCircle className="w-3.5 h-3.5" />,
          label: "Missing",
        };
    }
  };

  const uploadedCount = Object.values(documents).filter(doc => doc.documentId).length;
  const approvedCount = Object.values(documents).filter(doc => doc.status === "Approved").length;
  const totalRequired = 4;
  const progressPercentage = (uploadedCount / totalRequired) * 100;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-600 via-indigo-600 to-violet-700 p-6 md:p-8 text-white shadow-lg">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white/15 backdrop-blur rounded-2xl shadow-inner">
              <FileText className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">Document management</h1>
              <p className="text-blue-100 text-sm mt-1">
                Upload and manage the documents required to complete your residence application
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-white/10 backdrop-blur rounded-2xl px-5 py-3 border border-white/20">
            <div className="text-right">
              <p className="text-xs text-blue-100">Overall progress</p>
              <p className="text-2xl font-bold text-white">{Math.round(progressPercentage)}%</p>
            </div>
            <div className="relative w-12 h-12">
              <svg className="w-12 h-12 -rotate-90" viewBox="0 0 36 36">
                <circle cx="18" cy="18" r="15.5" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="3" />
                <circle
                  cx="18" cy="18" r="15.5" fill="none" stroke="white" strokeWidth="3"
                  strokeDasharray={`${progressPercentage} ${100 - progressPercentage}`}
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5 shadow-sm border border-blue-100 hover:shadow-md transition-all duration-200">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2.5 bg-white rounded-xl shadow-sm">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
          </div>
          <p className="text-sm text-blue-700 font-medium">Documents uploaded</p>
          <p className="text-3xl font-bold text-blue-900 mt-1">{uploadedCount} <span className="text-base font-medium text-blue-500">/ {totalRequired}</span></p>
          <p className="text-xs text-blue-500 mt-2">Required for your application</p>
        </div>

        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-5 shadow-sm border border-emerald-100 hover:shadow-md transition-all duration-200">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2.5 bg-white rounded-xl shadow-sm">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <span className="text-xs font-medium text-emerald-700 bg-white/70 px-2 py-1 rounded-full">
              {approvedCount}/{totalRequired} verified
            </span>
          </div>
          <p className="text-sm text-emerald-700 font-medium">Completion</p>
          <p className="text-3xl font-bold text-emerald-900 mt-1">{Math.round(progressPercentage)}%</p>
          <div className="w-full bg-white/60 rounded-full h-2 mt-3">
            <div
              className="bg-emerald-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        <div className="bg-gradient-to-br from-violet-50 to-violet-100 rounded-xl p-5 shadow-sm border border-violet-100 hover:shadow-md transition-all duration-200">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2.5 bg-white rounded-xl shadow-sm">
              <Shield className="w-5 h-5 text-violet-600" />
            </div>
          </div>
          <p className="text-sm text-violet-700 font-medium">Storage</p>
          <p className="text-lg font-bold text-violet-900 mt-1">Encrypted &amp; secure</p>
          <p className="text-xs text-violet-500 mt-2">PDF, JPG, PNG &middot; up to 10MB each</p>
        </div>
      </div>

      {progressPercentage === 100 && (
        <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl px-5 py-4 flex items-center gap-3 text-emerald-700 text-sm shadow-sm">
          <div className="p-1.5 bg-emerald-500 rounded-full">
            <CheckCircle className="w-4 h-4 text-white" />
          </div>
          <span className="font-medium">All required documents have been uploaded. We'll notify you once they're reviewed.</span>
        </div>
      )}

      {/* Document Cards */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Required documents</h3>
            <p className="text-sm text-gray-500 mt-0.5">Upload each document below and track its review status</p>
          </div>
          <span className="text-xs font-medium text-indigo-700 bg-indigo-50 px-3 py-1.5 rounded-full border border-indigo-100">
            {documentList.length} documents
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {documentList.map((doc) => {
            const docData = documents[doc.key];
            const statusStyle = getStatusStyle(docData.status);
            const accent = accentStyles[doc.accent];
            const isApproved = docData.status === "Approved";
            const isUploaded = docData.status === "Uploaded";
            const hasFile = docData.file || docData.documentId;
            const isUploading = uploadingKey === doc.key;

            return (
              <div
                key={doc.key}
                className={`bg-white rounded-xl shadow-sm border border-gray-200 border-l-4 ${accent.border} p-5 hover:shadow-md transition-all duration-200`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${accent.iconBg}`}>
                      <span className={accent.iconText}>{doc.icon}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{doc.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{doc.description}</p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text} border ${statusStyle.border} flex-shrink-0`}>
                    {statusStyle.icon}
                    {statusStyle.label}
                  </span>
                </div>

                <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-2 min-w-0">
                    {docData.fileName ? (
                      <>
                        <File className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-sm text-gray-600 truncate">
                          {docData.fileName.length > 28 ? docData.fileName.substring(0, 25) + "..." : docData.fileName}
                        </span>
                      </>
                    ) : (
                      <span className="text-sm text-gray-400">No file selected</span>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      ref={fileRefs[doc.key]}
                      hidden
                      onChange={(e) => handleFileChange(e, doc.key)}
                    />

                    {!isApproved && (
                      <button
                        onClick={() => fileRefs[doc.key].current.click()}
                        className={`p-2 rounded-lg transition-all duration-200 ${accent.iconText} ${accent.hover}`}
                        title="Select file"
                      >
                        <Upload className="w-4 h-4" />
                      </button>
                    )}

                    {hasFile && (
                      <button
                        onClick={() => handleView(doc.key)}
                        className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all duration-200"
                        title="Preview"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    )}

                    {docData.file && !isUploaded && !isApproved && (
                      <button
                        onClick={() => handleUpload(doc.key)}
                        disabled={isUploading}
                        className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 shadow-sm"
                      >
                        {isUploading ? "Uploading..." : "Upload"}
                      </button>
                    )}

                    {isApproved && (
                      <span className="inline-flex items-center gap-1.5 text-xs text-green-700 font-medium bg-green-50 px-3 py-1.5 rounded-lg border border-green-200">
                        <CheckCircle className="w-3.5 h-3.5" />
                        Verified
                      </span>
                    )}

                    {isUploaded && !isApproved && (
                      <span className="text-xs text-blue-700 font-medium bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-200">
                        Pending review
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Security Notice */}
      <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl shadow-sm border border-gray-200 p-5">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-white rounded-lg shadow-sm">
              <Shield className="w-4 h-4 text-indigo-600" />
            </div>
            <span>Documents are stored securely with encryption</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-1.5 h-1.5 bg-indigo-300 rounded-full" />
            <span>Max file size: 10MB</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-1.5 h-1.5 bg-indigo-300 rounded-full" />
            <span>Accepted formats: PDF, JPG, PNG</span>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {previewFile && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-indigo-600">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/15 rounded-lg">
                  <FileText className="w-4 h-4 text-white" />
                </div>
                <h2 className="font-semibold text-white text-base">Document preview</h2>
              </div>
              <button
                onClick={closeModal}
                className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-white/15 transition-all duration-200"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            <div className="p-6 flex justify-center bg-gray-50 min-h-[500px]">
              {previewType === "pdf" ? (
                <iframe
                  src={previewFile}
                  title="Document Preview"
                  className="w-full h-[75vh] rounded-xl shadow-sm border border-gray-200"
                />
              ) : (
                <img
                  src={previewFile}
                  alt="Document Preview"
                  className="max-h-[75vh] object-contain rounded-xl shadow-sm border border-gray-200"
                />
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end bg-white">
              <button
                onClick={closeModal}
                className="px-5 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-sm"
              >
                Close preview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documents;